import type { CSSProperties } from 'react';
import { CUPERTINO_CODEPOINTS, type CupertinoIconName } from './cupertinoCodepoints';
import styles from './Icon.module.css';

export type { CupertinoIconName } from './cupertinoCodepoints';
export type IconName = CupertinoIconName;

/**
 * The 8 Material icons the Flutter app uses, mapped to Cupertino look-alikes
 * (key = Flutter `Icons.*` name).
 */
export const MATERIAL_FALLBACKS = {
  add: 'add',
  chevron_left: 'chevron_left',
  chevron_right: 'chevron_right',
  keyboard_arrow_down_rounded: 'chevron_down',
  check_circle_rounded: 'checkmark_circle_fill',
  flag_rounded: 'flag_fill',
  insights_rounded: 'chart_bar_alt_fill',
  remove_circle_outline: 'minus_circle',
} as const satisfies Record<string, CupertinoIconName>;

export interface IconProps {
  name: CupertinoIconName;
  /** Glyph size in px (Flutter default 24). */
  size?: number;
  /** Any CSS colour; defaults to `currentColor`. */
  color?: string;
  className?: string;
  style?: CSSProperties;
  /** Accessible label; when omitted the icon is decorative (aria-hidden). */
  title?: string;
  'aria-label'?: string;
}

/** Renders a Flutter CupertinoIcons glyph from the bundled icon font. */
export function Icon({ name, size = 24, color, className, style, title, 'aria-label': ariaLabel }: IconProps) {
  const label = ariaLabel ?? title;
  const glyph = String.fromCodePoint(CUPERTINO_CODEPOINTS[name]);
  return (
    <span
      className={className ? `${styles.icon} ${className}` : styles.icon}
      style={{ fontSize: size, width: size, height: size, color, ...style }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      title={title}
      data-icon={name}
    >
      {glyph}
    </span>
  );
}
